import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Stepper,
  Step,
  StepLabel,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { pb, getCurrentUser } from "../services/pocketbase";

const StaffApplication = () => {
  const navigate = useNavigate();
  const user = getCurrentUser();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  // Form data state
  const [formData, setFormData] = useState({
    // Section 1 (from PocketBase)
    username: user?.username || "",
    discord_id: user?.discord_id || "",
    application_type: "", // Add application type field

    // Section 2
    in_game_name: "",
    age: "",
    country_timezone: "",
    tog_membership_duration: "",

    // Section 3
    why_staff: "",
    previous_leadership: "",
    skills_qualities: "",
    conflict_handling: "",
    activity_dedication: "",

    // Section 4
    tog_requirements: "",
    report_problem: "",
    discrimination_policy: "",
    break_steps: "",
    strike_policy: "",
    sergeant_other_community: "",
    transfer_requirements: "",
    missed_meetings: "",
    dating_policy: "",
    prohibited_actions: "",

    // Section 5
    agree_to_rules: false,
  });

  // Validation state for each section
  const [sectionValid, setSectionValid] = useState({
    0: false,
    1: false,
    2: false,
    3: false,
    4: false,
  });

  // Steps for the stepper
  const steps = [
    "User Information",
    "Personal Details",
    "Experience & Qualities",
    "Code of Conduct Knowledge",
    "Agreement",
  ];

  useEffect(() => {
    // Redirect to login if not authenticated
    if (!pb.authStore.isValid) {
      navigate("/login");
    }
  }, [navigate]);

  // Validate section 1
  useEffect(() => {
    const { application_type } = formData;
    const isValid = application_type.trim() !== "";

    setSectionValid((prev) => ({ ...prev, 0: isValid }));
    // eslint-disable-next-line
  }, [formData.application_type]);

  // Validate section 2
  useEffect(() => {
    const { in_game_name, age, country_timezone, tog_membership_duration } =
      formData;
    const isValid =
      in_game_name.trim() !== "" &&
      age.trim() !== "" &&
      country_timezone.trim() !== "" &&
      tog_membership_duration.trim() !== "";

    setSectionValid((prev) => ({ ...prev, 1: isValid }));
    // eslint-disable-next-line
  }, [
    formData.in_game_name,
    formData.age,
    formData.country_timezone,
    formData.tog_membership_duration,
  ]);

  // Validate section 3
  useEffect(() => {
    const {
      why_staff,
      previous_leadership,
      skills_qualities,
      conflict_handling,
      activity_dedication,
    } = formData;
    const isValid =
      why_staff.trim() !== "" &&
      previous_leadership.trim() !== "" &&
      skills_qualities.trim() !== "" &&
      conflict_handling.trim() !== "" &&
      activity_dedication.trim() !== "";

    setSectionValid((prev) => ({ ...prev, 2: isValid }));
    // eslint-disable-next-line
  }, [
    formData.why_staff,
    formData.previous_leadership,
    formData.skills_qualities,
    formData.conflict_handling,
    formData.activity_dedication,
  ]);

  // Validate section 4
  useEffect(() => {
    const {
      tog_requirements,
      report_problem,
      discrimination_policy,
      break_steps,
      strike_policy,
      sergeant_other_community,
      transfer_requirements,
      missed_meetings,
      dating_policy,
      prohibited_actions,
    } = formData;

    const isValid =
      tog_requirements.trim() !== "" &&
      report_problem.trim() !== "" &&
      discrimination_policy.trim() !== "" &&
      break_steps.trim() !== "" &&
      strike_policy.trim() !== "" &&
      sergeant_other_community.trim() !== "" &&
      transfer_requirements.trim() !== "" &&
      missed_meetings.trim() !== "" &&
      dating_policy.trim() !== "" &&
      prohibited_actions.trim() !== "";

    setSectionValid((prev) => ({ ...prev, 3: isValid }));
    // eslint-disable-next-line
  }, [
    formData.tog_requirements,
    formData.report_problem,
    formData.discrimination_policy,
    formData.break_steps,
    formData.strike_policy,
    formData.sergeant_other_community,
    formData.transfer_requirements,
    formData.missed_meetings,
    formData.dating_policy,
    formData.prohibited_actions,
  ]);

  // Validate section 5
  useEffect(() => {
    const { agree_to_rules } = formData;
    const isValid = agree_to_rules === true;

    setSectionValid((prev) => ({ ...prev, 4: isValid }));
    // eslint-disable-next-line
  }, [formData.agree_to_rules]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleNext = () => {
    if (activeStep < steps.length - 1) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep((prev) => prev - 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    // Check if user agreed to rules
    if (!formData.agree_to_rules) {
      setError(
        "You must agree to follow all TOG rules and policies to submit your application."
      );
      setLoading(false);
      return;
    }

    try {
      // Create Discord webhook message
      const webhookData = {
        content: "New Staff Application Submission!",
        embeds: [
          {
            title: "Staff Application",
            color: 0x00ff00,
            fields: [
              // Section 1
              {
                name: "Username",
                value: formData.username,
                inline: true,
              },
              {
                name: "Discord ID",
                value: formData.discord_id,
                inline: true,
              },
              {
                name: "Application Type",
                value: formData.application_type,
                inline: true,
              },
              // Section 2
              {
                name: "In-Game Name",
                value: formData.in_game_name,
                inline: true,
              },
              {
                name: "Age",
                value: formData.age,
                inline: true,
              },
              {
                name: "Country/Timezone",
                value: formData.country_timezone,
                inline: true,
              },
              {
                name: "TOG Membership Duration",
                value: formData.tog_membership_duration,
                inline: true,
              },
              // Section 3
              {
                name: "Why do you want to become a TOG Staff member?",
                value: formData.why_staff,
              },
              {
                name: "Previous Leadership Experience",
                value: formData.previous_leadership,
              },
              {
                name: "Skills & Qualities",
                value: formData.skills_qualities,
              },
              {
                name: "Conflict Handling",
                value: formData.conflict_handling,
              },
              {
                name: "Activity & Dedication",
                value: formData.activity_dedication,
              },
              // Section 4
              {
                name: "TOG Requirements",
                value: formData.tog_requirements,
              },
              {
                name: "Reporting Problems with Leaders",
                value: formData.report_problem,
              },
              {
                name: "Discrimination Policy",
                value: formData.discrimination_policy,
              },
              {
                name: "Break Steps",
                value: formData.break_steps,
              },
              {
                name: "Strike Policy",
                value: formData.strike_policy,
              },
              {
                name: "Sergeant in Other Communities",
                value: formData.sergeant_other_community,
              },
              {
                name: "Transfer Requirements",
                value: formData.transfer_requirements,
              },
              {
                name: "Missed Meetings",
                value: formData.missed_meetings,
              },
              {
                name: "Dating Policy",
                value: formData.dating_policy,
              },
              {
                name: "Prohibited Actions",
                value: formData.prohibited_actions,
              },
            ],
            timestamp: new Date().toISOString(),
          },
        ],
      };

      const response = await fetch("/api/webhooks/staff-application", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(webhookData),
      });

      if (!response.ok) {
        throw new Error("Failed to send application.");
      }

      setSuccess(true);
      setLoading(false);
    } catch (err) {
      console.error("Error submitting application:", err);
      setError(err.message || "Failed to submit application");
      setLoading(false);
    }
  };

  // Render different form sections based on active step
  const renderFormSection = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              User Information
            </Typography>
            <TextField
              fullWidth
              label="Username"
              value={formData.username}
              disabled
              margin="normal"
            />
            <TextField
              fullWidth
              label="Discord ID"
              value={formData.discord_id}
              disabled
              margin="normal"
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Application Type</InputLabel>
              <Select
                name="application_type"
                value={formData.application_type}
                onChange={handleInputChange}
                required
              >
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="moderator">Moderator</MenuItem>
                <MenuItem value="event_coordinator">Event Coordinator</MenuItem>
                <MenuItem value="community_manager">Community Manager</MenuItem>
              </Select>
            </FormControl>
          </Box>
        );
      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Personal Details
            </Typography>
            <TextField
              fullWidth
              label="In-Game Name"
              name="in_game_name"
              value={formData.in_game_name}
              onChange={handleInputChange}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Age"
              name="age"
              value={formData.age}
              onChange={handleInputChange}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Country/Timezone"
              name="country_timezone"
              value={formData.country_timezone}
              onChange={handleInputChange}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="How long have you been a TOG member?"
              name="tog_membership_duration"
              value={formData.tog_membership_duration}
              onChange={handleInputChange}
              margin="normal"
              required
            />
          </Box>
        );
      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Experience & Qualities
            </Typography>
            <TextField
              fullWidth
              label="Why do you want to become a TOG Staff member?"
              name="why_staff"
              value={formData.why_staff}
              onChange={handleInputChange}
              margin="normal"
              multiline
              rows={3}
              required
            />
            <TextField
              fullWidth
              label="Do you have any previous leadership experience? If so, please describe."
              name="previous_leadership"
              value={formData.previous_leadership}
              onChange={handleInputChange}
              margin="normal"
              multiline
              rows={3}
              required
            />
            <TextField
              fullWidth
              label="What skills and qualities do you possess that would make you a good staff member?"
              name="skills_qualities"
              value={formData.skills_qualities}
              onChange={handleInputChange}
              margin="normal"
              multiline
              rows={3}
              required
            />
            <TextField
              fullWidth
              label="How would you handle a conflict between members?"
              name="conflict_handling"
              value={formData.conflict_handling}
              onChange={handleInputChange}
              margin="normal"
              multiline
              rows={3}
              required
            />
            <TextField
              fullWidth
              label="How much time can you dedicate to TOG activities each week?"
              name="activity_dedication"
              value={formData.activity_dedication}
              onChange={handleInputChange}
              margin="normal"
              multiline
              rows={3}
              required
            />
          </Box>
        );
      case 3:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Code of Conduct Knowledge
            </Typography>
            <TextField
              fullWidth
              label="What are the 3 main requirements to join TOG?"
              name="tog_requirements"
              value={formData.tog_requirements}
              onChange={handleInputChange}
              margin="normal"
              multiline
              rows={3}
              required
            />
            <TextField
              fullWidth
              label="What is the proper way to report a problem with a leader?"
              name="report_problem"
              value={formData.report_problem}
              onChange={handleInputChange}
              margin="normal"
              multiline
              rows={3}
              required
            />
            <TextField
              fullWidth
              label="What is TOG's policy on discrimination, harassment, and toxicity?"
              name="discrimination_policy"
              value={formData.discrimination_policy}
              onChange={handleInputChange}
              margin="normal"
              multiline
              rows={3}
              required
            />
            <TextField
              fullWidth
              label="If you need to take a break from TOG, what steps must you take?"
              name="break_steps"
              value={formData.break_steps}
              onChange={handleInputChange}
              margin="normal"
              multiline
              rows={3}
              required
            />
            <TextField
              fullWidth
              label="What is the 3 Strike Policy, and what happens at each stage?"
              name="strike_policy"
              value={formData.strike_policy}
              onChange={handleInputChange}
              margin="normal"
              multiline
              rows={3}
              required
            />
            <TextField
              fullWidth
              label="Can a Sergeant rank or higher be a member of another gaming community? Why or why not?"
              name="sergeant_other_community"
              value={formData.sergeant_other_community}
              onChange={handleInputChange}
              margin="normal"
              multiline
              rows={3}
              required
            />
            <TextField
              fullWidth
              label="What is required for a squad or division transfer to be accepted?"
              name="transfer_requirements"
              value={formData.transfer_requirements}
              onChange={handleInputChange}
              margin="normal"
              multiline
              rows={3}
              required
            />
            <TextField
              fullWidth
              label="If a member repeatedly misses meetings or game nights, what should they do?"
              name="missed_meetings"
              value={formData.missed_meetings}
              onChange={handleInputChange}
              margin="normal"
              multiline
              rows={3}
              required
            />
            <TextField
              fullWidth
              label="What are TOG's policies regarding dating within the community?"
              name="dating_policy"
              value={formData.dating_policy}
              onChange={handleInputChange}
              margin="normal"
              multiline
              rows={3}
              required
            />
            <TextField
              fullWidth
              label="What actions are strictly prohibited under TOG's Online Safety Policy?"
              name="prohibited_actions"
              value={formData.prohibited_actions}
              onChange={handleInputChange}
              margin="normal"
              multiline
              rows={3}
              required
            />
          </Box>
        );
      case 4:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Agreement
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  name="agree_to_rules"
                  checked={formData.agree_to_rules}
                  onChange={handleCheckboxChange}
                  required
                />
              }
              label="I agree to follow all TOG rules and policies"
            />
            {!formData.agree_to_rules && (
              <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                You must agree to follow all TOG rules and policies to submit
                your application.
              </Typography>
            )}
          </Box>
        );
      default:
        return null;
    }
  };

  if (success) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 3, backgroundColor: "rgba(40, 44, 52, 0.99)" }}>
          <Typography variant="h4" gutterBottom>
            Application Submitted
          </Typography>
          <Alert severity="success" sx={{ mt: 2, mb: 2 }}>
            Your staff application has been submitted successfully. We will
            review your application and contact you soon.
          </Alert>
          <Button variant="contained" onClick={() => navigate("/")}>
            Return to Home
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container
      maxWidth="md"
      sx={{
        mt: 4,
        mb: 4,
        borderRadius: 2,
        backgroundColor: "rgba(40, 44, 52, 0.99)",
      }}
    >
      <Box sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom align="center">
          Staff Application
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Stepper
          activeStep={activeStep}
          sx={{
            mb: 4,
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
            "& .MuiStep-root": {
              mb: { xs: 2, sm: 0 },
            },
          }}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <form onSubmit={handleSubmit}>
          {renderFormSection()}

          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
            <Button
              variant="outlined"
              onClick={handleBack}
              disabled={activeStep === 0}
            >
              Back
            </Button>

            <Box>
              {activeStep === steps.length - 1 ? (
                <Button
                  type="submit"
                  variant="contained"
                  disabled={!sectionValid[activeStep] || loading}
                  sx={{ ml: 1 }}
                >
                  {loading ? <CircularProgress size={24} /> : "Submit"}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={!sectionValid[activeStep]}
                  sx={{ ml: 1 }}
                >
                  Next
                </Button>
              )}
            </Box>
          </Box>
        </form>
      </Box>
    </Container>
  );
};

export default StaffApplication;
